/**
 * useLogFoot - how tall the log stands at the map's foot, for the map's furniture to clear it (2026-10-21).
 *
 * Rolled up, the log is its edge (the ticker, `--log-edge-h`) PLUS the vox under it (the chat chips and the
 * composer): about 80 px with the shared log, not the 24 px the stylesheet assumed - so the scene bar, the deploy
 * bar, the readout and the camera's instruments sat under it, and the scene rail covered its controls. Opened, it
 * adds its scroller, which covers the map by design (the bottom bar's four sit on the edge itself since 2026-10-22). This measures the overlay without its scroller
 * and publishes it as `--log-foot-h` on the container, straight onto its style and only when it changes: a value
 * written every frame must never be React state (gotchas, "Maximum update depth"). Folded, the log is the whole
 * panel and nothing below it is reachable anyway: 0.
 *
 * @param {object} refs
 * @param {{ current: HTMLElement | null }} refs.containerRef - the map and the log (`.splitContainer`)
 * @param {{ current: HTMLElement | null }} refs.overlayRef - the log overlay
 * @param {{ current: HTMLElement | null }} refs.scrollWrapRef - the scroller's wrap inside it
 * @param {object} o
 * @param {boolean} o.folded - the map folded behind the log
 * @param {string} o.layoutKey - changes when the overlay's parts mount or unmount (rolled up, the view)
 */
import { useLayoutEffect } from 'react';

export default function useLogFoot({ containerRef, overlayRef, scrollWrapRef }, { folded, layoutKey }) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    const overlay = overlayRef.current;
    if (!container || !overlay) return undefined;
    let last = null;
    const publish = () => {
      const foot = folded ? 0 : overlay.offsetHeight - (scrollWrapRef.current?.offsetHeight ?? 0);
      const value = `${Math.max(0, foot)}px`;
      if (value === last) return;
      last = value;
      container.style.setProperty('--log-foot-h', value);
    };
    publish();
    // Absent in jsdom (no polyfill in src/test/setup.js): the first measure is all a test without one sees.
    const Observer = globalThis.ResizeObserver;
    if (!Observer) return undefined;
    const observer = new Observer(publish);
    // Each part, not just the overlay: an opened log keeps its height while the vox inside it grows or goes.
    for (const el of [overlay, ...overlay.children]) observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef, overlayRef, scrollWrapRef, folded, layoutKey]);
}
